/* Append catalog pages while retaining the theme's native filtering and cards. */
if (!customElements.get('tandoori-infinite-scroll')) {
  customElements.define('tandoori-infinite-scroll', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.next = this.querySelector('.page-number-next')?.href;
      this.button = this.querySelector('[data-scroll-more]');
      this.label = this.querySelector('[data-scroll-label]') || this.button;
      this.status = this.querySelector('[data-scroll-status]');
      this.fallback = this.querySelector('[data-scroll-fallback]');
      this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
      this.moreText = this.label?.textContent.trim();
      this.skeletons = [];
      // "Back to browsing controls" scrolls instead of following its #fragment: a fragment jump
      // adds a history entry, and facets.js answers that popstate by restoring the page-load results.
      this.back = this.querySelector('.tb-catalog-back');
      this.onBack = (event) => {
        const target = document.getElementById('ProductGridContainer');
        if (!target || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        // Scrolling up reveals the sticky header, so stop below it with a little room to spare.
        const header = document.querySelector('sticky-header:not([disabled])') ? document.querySelector('.section-header') : null;
        const top = target.getBoundingClientRect().top + window.scrollY - (header?.offsetHeight || 0) - 16;
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'instant' : 'smooth' });
        target.focus({ preventScroll: true });
      };
      this.back?.addEventListener('click', this.onBack);
      this.updateProgress();
      if (!this.grid || !this.next || !this.button || !('IntersectionObserver' in window)) return;
      this.fallback.hidden = true;
      this.button.hidden = false;
      // A deliberate press (mouse, Enter or Space) moves focus to the first new book;
      // loads started by scrolling never move focus.
      this.onClick = () => {
        this.focusNew = true;
        this.load();
      };
      this.button.addEventListener('click', this.onClick);
      // Load up to three pages automatically; after that the "Load more" button stays,
      // so the footer (addresses, phone, policies) can still be reached by scrolling.
      this.autoLoads = 0;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || this.busy) return;
        if (this.autoLoads >= 3) {
          this.observer.disconnect();
          return;
        }
        this.autoLoads += 1;
        this.load();
      }, { rootMargin: '0px 0px 450px 0px' });
      this.observer.observe(this.button);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.button?.removeEventListener('click', this.onClick);
      this.back?.removeEventListener('click', this.onBack);
      this.removeSkeletons();
      this.ready = false;
    }

    updateProgress() {
      const progress = this.querySelector('[data-scroll-progress]');
      if (!progress || !this.grid) return;
      // A page opened at ?page=N starts after the products of the earlier pages;
      // an empty grid (e.g. a stale ?page=99 link) shows no progress at all.
      const cards = this.grid.querySelectorAll('product-card[data-product-id]').length;
      const total = Number(this.dataset.total);
      if (!cards || !Number.isFinite(total) || total <= 0) return;
      const count = Math.min((Number(this.dataset.offset) || 0) + cards, total);
      progress.hidden = false;
      this.querySelector('[data-scroll-count]').textContent = this.dataset.progressLabel.replace('[count]', count).replace('[total]', total);
      const bar = progress.querySelector('progress');
      if (bar) bar.value = Math.min(count, total);
      progress.querySelector('[data-scroll-fill]')?.style.setProperty('--tb-progress', String(Math.min(count / total, 1)));
    }

    columns() {
      const items = [...this.grid.querySelectorAll(':scope > .grid-item:not(.grid-item-block)')].filter((item) => item.offsetParent);
      if (!items.length) return 2;
      const top = items[0].offsetTop;
      let count = 0;
      for (const item of items) {
        if (Math.abs(item.offsetTop - top) > 2) break;
        count += 1;
      }
      return Math.max(1, count);
    }

    showSkeletons() {
      this.removeSkeletons();
      const count = Math.min(4, this.columns());
      for (let index = 0; index < count; index += 1) {
        const item = document.createElement('li');
        item.className = 'grid-item tb-skeleton-item';
        item.setAttribute('aria-hidden', 'true');
        // Reuse the card and media classes so placeholders match the real cards' spacing.
        const card = document.createElement('div');
        card.className = 'card card-product tb-skeleton';
        const media = document.createElement('div');
        media.className = 'card-media tb-skeleton__media';
        media.append(Object.assign(document.createElement('span'), { className: 'tb-skeleton__cover' }));
        card.append(media);
        for (const className of ['tb-skeleton__line', 'tb-skeleton__line tb-skeleton__line--short', 'tb-skeleton__line tb-skeleton__line--price']) {
          card.append(Object.assign(document.createElement('span'), { className }));
        }
        item.append(card);
        this.skeletons.push(item);
      }
      this.grid.append(...this.skeletons);
    }

    removeSkeletons() {
      this.skeletons?.forEach((item) => item.remove());
      this.skeletons = [];
    }

    reveal(nodes) {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      nodes.filter((node) => node instanceof HTMLElement).forEach((node, index) => {
        node.style.setProperty('--tb-arrive-delay', `${Math.min(index, 9) * 35}ms`);
        node.classList.add('tb-arrive');
        const done = (event) => {
          if (event.target !== node) return; // Ignore animations bubbling up from inside the card.
          node.classList.remove('tb-arrive');
          node.style.removeProperty('--tb-arrive-delay');
          node.removeEventListener('animationend', done);
          node.removeEventListener('animationcancel', done);
        };
        node.addEventListener('animationend', done);
        node.addEventListener('animationcancel', done);
      });
    }

    focusFirst(nodes) {
      const first = nodes.find((node) => node.querySelector?.('product-card'));
      const target = first?.querySelector('.card-heading a[href]') || first?.querySelector('a[href]');
      target?.focus();
    }

    setBusy(busy) {
      this.busy = busy;
      this.classList.toggle('is-loading', busy);
      if (busy) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');
      if (busy) this.grid.setAttribute('aria-busy', 'true');
      else this.grid.removeAttribute('aria-busy');
    }

    async load() {
      // aria-disabled (not disabled) keeps keyboard focus on the button while a page loads.
      if (this.busy || !this.next || !this.isConnected || this.closest('.collection.loading')) return;
      this.setBusy(true);
      this.classList.remove('is-error');
      this.label.textContent = this.moreText;
      this.status.textContent = this.dataset.loading;
      this.showSkeletons();
      const locationAtStart = location.href;
      const url = new URL(this.next, location.origin);
      url.searchParams.set('section_id', this.dataset.section);
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Page request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = html.querySelector('#product-grid');
        if (!incoming || !incoming.children.length) throw new Error('Missing product grid');
        if (!this.isConnected || location.href !== locationAtStart || this.closest('.collection.loading')) return;
        this.removeSkeletons();
        const added = [...incoming.children];
        this.grid.append(...added);
        this.reveal(added);
        this.updateProgress();
        this.next = html.querySelector('tandoori-infinite-scroll .page-number-next')?.getAttribute('href');
        // Keep the non-JavaScript recovery links aligned with the latest page.
        const fallback = html.querySelector('[data-scroll-fallback]');
        if (fallback) this.fallback.replaceChildren(...fallback.childNodes);
        this.fallback.hidden = true;
        const books = added.filter((node) => node.querySelector?.('product-card[data-product-id]')).length;
        const loaded = this.dataset.loadedCount?.includes('[count]') ? this.dataset.loadedCount.replace('[count]', books) : this.dataset.loaded;
        this.status.textContent = this.next ? loaded : `${loaded} ${this.dataset.done}`;
        if (this.failed) {
          this.failed = false;
          if (this.next) this.observer.observe(this.button);
        }
        if (!this.next) {
          this.observer.disconnect();
          this.button.hidden = true;
        }
        if (this.focusNew) this.focusFirst(added);
      } catch {
        this.removeSkeletons();
        if (!this.isConnected) return;
        // Stop automatic retries; the button becomes an explicit "Try again".
        this.observer.disconnect();
        this.failed = true;
        this.classList.add('is-error');
        this.status.textContent = this.dataset.error;
        this.label.textContent = this.dataset.retry || this.moreText;
        this.fallback.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.removeSkeletons();
        this.focusNew = false;
        if (this.isConnected) this.setBusy(false);
        else this.busy = false;
      }
    }
  });
}
